import NiceModal from '@ebay/nice-modal-react';

import { Suspense, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import Button from 'components/field/Button';
import Authorization from 'components/global/Authorization';
import Loading from 'components/global/Loading';
import PageHeader from 'components/global/PageHeader';
import Tabs from 'components/global/Tabs';

import useModalRouter from 'hooks/useModalRouter';

import ImportDataModal from 'modals/ImportDataModal';

import { IMPORT_EXPORT } from 'services/consts';

import PATHS, { mergePaths } from 'utils/paths';

import { faArrowUpWideShort, faDownload, faPlus } from '@fortawesome/pro-solid-svg-icons';

const HeaderButtons = () => {
	const { pathname } = useLocation();
	const { showModal } = useModalRouter();

	const acceptedFileTypes = useMemo(() => ['.xlsx', '.xls'], []);

	return (
		<>
			{pathname === mergePaths(PATHS.ADDRESS_MANAGEMENT, PATHS.COUNTRIES) && (
				<>
					<Authorization path="/modal/country-import">
						<Button
							icon={faDownload}
							variant="borderless"
							label="importData"
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
							onClick={() =>
								NiceModal.show(ImportDataModal, {
									title: 'countryImport',
									acceptedFileTypes,
									icon: faArrowUpWideShort,
									downloadUrl: IMPORT_EXPORT.GET_COUNTRY_TEMPLATE,
									uploadUrl: IMPORT_EXPORT.IMPORT_COUNTRY
								})
							}
							className="mobile-hidden importDataButton"
						/>
					</Authorization>

					<Authorization path="/modal/country-form">
						<Button
							icon={faPlus}
							label="addCountry"
							onClick={() => {
								showModal('country-form');
							}}
							variant="blue"
						/>
					</Authorization>
				</>
			)}

			{pathname === mergePaths(PATHS.ADDRESS_MANAGEMENT, PATHS.STATES) && (
				<>
					<Authorization path="/modal/state-import">
						<Button
							icon={faDownload}
							variant="borderless"
							label="importData"
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
							onClick={() =>
								NiceModal.show(ImportDataModal, {
									title: 'stateImport',
									acceptedFileTypes,
									icon: faArrowUpWideShort,
									downloadUrl: IMPORT_EXPORT.GET_STATE_TEMPLATE,
									uploadUrl: IMPORT_EXPORT.IMPORT_STATE
								})
							}
							className="mobile-hidden importDataButton"
						/>
					</Authorization>

					<Authorization path="/modal/state-form">
						<Button
							icon={faPlus}
							label="addState"
							onClick={() => {
								showModal('state-form');
							}}
							variant="blue"
						/>
					</Authorization>
				</>
			)}

			{pathname === mergePaths(PATHS.ADDRESS_MANAGEMENT, PATHS.GOVERNORATES) && (
				<>
					<Authorization path="/modal/governorate-form">
						<Button
							icon={faPlus}
							label="addGovernorate"
							onClick={() => {
								showModal('governorate-form');
							}}
							variant="blue"
						/>
					</Authorization>
				</>
			)}

			{pathname === mergePaths(PATHS.ADDRESS_MANAGEMENT, PATHS.CITIES) && (
				<>
					<Authorization path="/modal/city-import">
						<Button
							icon={faDownload}
							variant="borderless"
							label="importData"
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
							onClick={() =>
								NiceModal.show(ImportDataModal, {
									title: 'cityImport',
									acceptedFileTypes,
									icon: faArrowUpWideShort,
									downloadUrl: IMPORT_EXPORT.GET_CITY_TEMPLATE,
									uploadUrl: IMPORT_EXPORT.IMPORT_CITY
								})
							}
							className="mobile-hidden importDataButton"
						/>
					</Authorization>
					<Authorization path="/modal/city-form">
						<Button
							icon={faPlus}
							label="addCity"
							onClick={() => {
								showModal('city-form');
							}}
							variant="blue"
						/>
					</Authorization>
				</>
			)}

			{pathname === mergePaths(PATHS.ADDRESS_MANAGEMENT, PATHS.DISTRICTS) && (
				<>
					<Authorization path="/modal/district-import">
						<Button
							icon={faDownload}
							variant="borderless"
							label="importData"
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
							onClick={() =>
								NiceModal.show(ImportDataModal, {
									title: 'districtImport',
									acceptedFileTypes,
									icon: faArrowUpWideShort,
									downloadUrl: IMPORT_EXPORT.GET_DISTRICT_TEMPLATE,
									uploadUrl: IMPORT_EXPORT.IMPORT_DISTRICT
								})
							}
							className="mobile-hidden importDataButton"
						/>
					</Authorization>
					<Authorization path="/modal/district-form">
						<Button
							icon={faPlus}
							label="addDistrict"
							onClick={() => {
								showModal('district-form');
							}}
							variant="blue"
						/>
					</Authorization>
				</>
			)}
			{pathname === mergePaths(PATHS.ADDRESS_MANAGEMENT, PATHS.NEIGHBORHOODS) && (
				<>
					<Authorization path="/modal/neighbourhood-import">
						<Button
							icon={faDownload}
							variant="borderless"
							label="importData"
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
							onClick={() =>
								NiceModal.show(ImportDataModal, {
									title: 'neighbourhoodImport',
									acceptedFileTypes,
									icon: faArrowUpWideShort,
									downloadUrl: IMPORT_EXPORT.GET_NEIGHBORHOOD_TEMPLATE,
									uploadUrl: IMPORT_EXPORT.IMPORT_NEIGHBORHOOD
								})
							}
							className="mobile-hidden importDataButton"
						/>
					</Authorization>
					<Authorization path="/modal/neighbourhood-form">
						<Button
							icon={faPlus}
							label="addNeighbourhood"
							onClick={() => {
								showModal('neighbourhood-form');
							}}
							variant="blue"
						/>
					</Authorization>
				</>
			)}
		</>
	);
};

const AddressManagement = () => {
	const tabs = useMemo(
		() => [
			{
				label: 'countries',
				path: mergePaths(PATHS.ADDRESS_MANAGEMENT, PATHS.COUNTRIES)
			},
			{
				label: 'states',
				path: mergePaths(PATHS.ADDRESS_MANAGEMENT, PATHS.STATES)
			},
			{
				label: 'governorates',
				path: mergePaths(PATHS.ADDRESS_MANAGEMENT, PATHS.GOVERNORATES)
			},
			{
				label: 'cities',
				path: mergePaths(PATHS.ADDRESS_MANAGEMENT, PATHS.CITIES)
			},
			{
				label: 'districts',
				path: mergePaths(PATHS.ADDRESS_MANAGEMENT, PATHS.DISTRICTS)
			},
			{
				label: 'neighbourhoods',
				path: mergePaths(PATHS.ADDRESS_MANAGEMENT, PATHS.NEIGHBORHOODS)
			}
		],
		[]
	);

	return (
		<div>
			<PageHeader
				title="addressManagement"
				lordIcon={
					'https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/localization-icon.json.gz'
				}>
				<HeaderButtons />
			</PageHeader>
			<Tabs data={tabs} checkURL />
			<Suspense fallback={<Loading />}>
				<Outlet />
			</Suspense>
		</div>
	);
};

export default AddressManagement;
